<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useDateField, formatDocDate } from '@/shared/composables/dateUtils';
import { useOfficerAppointmentStore } from '../stores/officerAppointmentStore';
import { apiClient } from '@/shared/api/client';
import { useBaseCode } from '@/shared/composables/useBaseCode';
import type { BaseCode } from '@/shared/types/baseCode.types';
import TaskFlowSection from './TaskFlowSection.vue';
import type { TaskConfig } from '../api/taskConfigApi';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { useEntityDocVariables } from '@/shared/composables/useEntityDocVariables';
import { mapNewFinancialPeriodToB41Code } from '@/features/startTask/preset_task/sg/components/change-financial-year-end/composables/buildPresetChangeFyeTaskData';
import type { EntityItem } from '@/features/AI-documents/types/docGen.types';

const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Entity doc_variables 조회
const { getEntityData } = useEntityDocVariables();

// Props
interface Props {
  taskConfig?: TaskConfig | null;
  initialData?: any | null; // EFilingPage에서 TASK_DATA 전달용
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 완료/조회용)
  showTaskFlow?: boolean; // Task Flow 섹션 표시 여부
  eFilingMode?: boolean; // EFiling 모드 (입력 비활성화)
  validationErrors?: Record<string, string>; // 필드별 validation 에러 메시지
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  initialData: null,
  readOnly: false,
  showTaskFlow: true,
  eFilingMode: false,
  validationErrors: () => ({})
});

// Emits
const emit = defineEmits<{
  'update:taskFlow': [value: { all: boolean; createDocument: boolean; requestTSign: boolean; proceedWithEFiling: boolean }],
  'auto-select-templates': [config: { categoryName: string; templateIds: string[] }],
  'filter-select-templates': [config: { categoryName: string; templateIds: string[]; filterValue: string }],
  'reset-templates': [];
}>();

// Entity list (reuse existing store pattern from OfficerAppointmentForm)
const officerAppointmentStore = useOfficerAppointmentStore();
const entityCd = ref<string>('');

// 필드 refs (포커스 이동용)
const entityCdRef = ref<any>(null);
const revisedFyeInputRef = ref<any>(null);

// Entity profile fields (from L_ENTITY via /startTask/common/entity/:entityCd/info)
const entityProfile = ref<any | null>(null);

// 내부 validation 상태 관리
const internalValidationErrors = ref<Record<string, string>>({});

// 폼 데이터 객체 (화면에서 입력된 모든 정보 저장)
const formData = ref({
  // Entity 선택
  entityCd: '',
  // Revised financial year end date (YYYY-MM-DD 형식)
  revisedFinancialYearEndDate: '',
  // Financial year period (B41001/B41002)
  financialYearPeriod: ''
});

const regNo = computed(() => entityProfile.value?.regNo ?? '');
const companyName = computed(() => entityProfile.value?.companyName ?? '');
const financialYearStart = computed(() => entityProfile.value?.financialYearStartDisplay ?? '');
const financialYearEnd = computed(() => entityProfile.value?.financialYearEndDisplay ?? '');
const agmDueDate = computed(() => entityProfile.value?.agmDuedateDisplay ?? '');
const arDueDate = computed(() => entityProfile.value?.arDuedateDisplay ?? '');

// Revised FYE date input (keep calendar input behavior)
const {
  dateValue: revisedFyeValue,
  dateDisplay: revisedFyeDisplay,
  datePickerValue: revisedFyePickerValue,
  datePlaceholder,
  dateMenu: revisedFyeMenu,
  datePickerProps
} = useDateField();

const revisedFyeDisplayFormatted = computed(() => {
  const v = revisedFyeValue.value;
  return v ? formatDocDate(v) : '';
});

const addDaysToIsoDate = (isoDate: string, days: number) => {
  // isoDate: YYYY-MM-DD
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return '';
  const [y, m, d] = isoDate.split('-').map(n => Number(n));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

// Financial year period radio (keep as-is)
const { getCodesByMainCodeAuto } = useBaseCode();
const financialYearPeriodCodes = ref<BaseCode[]>([]);
const isLoadingFinancialYearPeriod = ref(false);
const financialYearPeriod = ref<string>(''); // TOTAL_CODE (예: B41001, B41002)

const financialYearPeriodOptions = computed(() => {
  return [...financialYearPeriodCodes.value]
    .sort((a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0))
    .map(code => ({
      value: code.totalCode,
      title: code.codeName || code.defaultCodeName || code.totalCode
    }));
});

const loadFinancialYearPeriodCodes = async () => {
  try {
    isLoadingFinancialYearPeriod.value = true;
    const codes = await getCodesByMainCodeAuto('B41');
    // SUBCODE가 '000'이 아닌 것만 필터링
    const filteredCodes = (codes || []).filter(c => c.subCode !== '000');
    
    // totalCode 기준으로 중복 제거 (Map 사용)
    const uniqueCodesMap = new Map<string, BaseCode>();
    filteredCodes.forEach(code => {
      const totalCode = code.totalCode || '';
      if (totalCode && !uniqueCodesMap.has(totalCode)) {
        uniqueCodesMap.set(totalCode, code);
      }
    });
    
    financialYearPeriodCodes.value = Array.from(uniqueCodesMap.values());

    // 기본값: B41001 (12 months) 우선, 없으면 첫 번째
    if (!financialYearPeriod.value) {
      const defaultCode =
        financialYearPeriodCodes.value.find(c => c.totalCode === 'B41001') ||
        financialYearPeriodCodes.value[0];
      if (defaultCode?.totalCode) {
        financialYearPeriod.value = defaultCode.totalCode;
      }
    }
  } catch (e) {
    console.warn('[ChangeFyeForm] Failed to load financial year period codes:', e);
    financialYearPeriodCodes.value = [];
  } finally {
    isLoadingFinancialYearPeriod.value = false;
  }
};

// 이미 자동 선택 이벤트를 발생시켰는지 추적
let hasEmittedAutoSelect = false;

// 카테고리/파일 자동 선택 함수 (filter 값이 없는 문서만 자동 선택)
const autoSelectCategoryAndFiles = () => {
  const taskConfig = props.taskConfig;

  if (!taskConfig) {
    return;
  }

  // 이미 같은 taskConfig로 이벤트를 발생시켰으면 스킵
  if (hasEmittedAutoSelect) {
    return;
  }

  try {
    // 문서(템플릿) 자동 선택 - filter가 없는 문서만
    if (taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // filter가 없는(null) 문서만 필터링
      const documentsWithoutFilter = taskConfig.documents.filter(
        doc => !doc.filter // filter가 null, undefined, '' 인 경우
      );

      if (documentsWithoutFilter.length > 0) {
        const templateIds = documentsWithoutFilter
          .map(doc => doc.DOC_TYPE)
          .filter(id => id); // null/undefined 제거

        // 카테고리명 = documents의 category (DIRECTORY에서 온 값)
        const categoryName = documentsWithoutFilter[0]?.category?.trim() || null;

        console.log('📋 [ChangeFyeForm] Filter 없는 문서 자동 선택:', {
          totalDocuments: taskConfig.documents.length,
          documentsWithoutFilter: documentsWithoutFilter.length,
          templateIds,
          categoryName
        });

        // 템플릿 자동 선택 이벤트 emit
        emit('auto-select-templates', {
          categoryName: categoryName || '',
          templateIds
        });

        // 이벤트 발생 완료 표시
        hasEmittedAutoSelect = true;
      } else {
        console.log('📋 [ChangeFyeForm] Filter 없는 문서가 없음');
        hasEmittedAutoSelect = true; // 이벤트는 발생시키지 않지만 플래그는 설정
      }
    }
  } catch (error) {
    console.error('[ChangeFyeForm] Failed to auto-select templates:', error);
  }
};

const loadEntityProfile = async (id: string) => {
  if (!id || !id.trim()) {
    entityProfile.value = null;
    return;
  }
  try {
    const res = await apiClient.get(`/startTask/change-fye/entity/${id.trim()}/info`);
    // apiClient wrapper returns AxiosResponse; support both {data} and direct object shapes
    entityProfile.value = (res as any)?.data?.data || (res as any)?.data || res;
  } catch (error) {
    console.error('[ChangeFyeForm] Failed to load entity info:', error);
    entityProfile.value = null;
  }
};

const normalizeText = (v: unknown) => String(v ?? '').trim().toUpperCase();

const resolveEntityCdFromFormData = async (rawFormData: any, fields: any): Promise<string> => {
  const directEntityCd =
    fields?.entity ||
    rawFormData?.entity ||
    fields?.entityCd ||
    rawFormData?.entityCd ||
    fields?.entity_cd ||
    rawFormData?.entity_cd ||
    rawFormData?.ENTITY_CD ||
    '';
  if (directEntityCd) return String(directEntityCd).trim();

  const regNoFromFormData =
    fields?.company_registration_no ||
    rawFormData?.company_registration_no ||
    fields?.companyRegistrationNo ||
    rawFormData?.companyRegistrationNo ||
    fields?.RegistrationNo?.value ||
    rawFormData?.RegistrationNo?.value ||
    '';
  const regNoKey = normalizeText(regNoFromFormData);
  if (!regNoKey) return '';

  if (!officerAppointmentStore.entities.length) {
    try {
      await officerAppointmentStore.fetchEntities();
    } catch (e) {
      console.warn('[ChangeFyeForm] Failed to fetch entities while resolving entityCd:', e);
    }
  }

  const matched = officerAppointmentStore.entities.find((ent: any) => {
    const entRegNo = normalizeText(ent?.regNo || ent?.REG_NO);
    return entRegNo && entRegNo === regNoKey;
  });

  return String(matched?.entityCd || matched?.ENTITY_CD || '').trim();
};

watch(entityCd, async (newCd) => {
  // Entity 변경 시 validation 에러 제거
  if (internalValidationErrors.value.entity) {
    delete internalValidationErrors.value.entity;
  }
  
  entityProfile.value = null;
  // formData 업데이트
  formData.value.entityCd = newCd || '';
  if (!newCd) return;
  try {
    await loadEntityProfile(newCd);
    // Entity가 선택되고 taskConfig가 있으면 문서 자동 선택
    if (props.taskConfig) {
      autoSelectCategoryAndFiles();
    }
  } catch (e) {
    // swallow: UI will just show empty fields
    console.warn('[ChangeFyeForm] Failed to load entity profile:', e);
  }
});

// Revised FYE date 변경 시 formData 업데이트 및 validation 에러 제거
watch(revisedFyeValue, (newValue) => {
  if (internalValidationErrors.value.revisedFinancialYearEndDate) {
    delete internalValidationErrors.value.revisedFinancialYearEndDate;
  }
  formData.value.revisedFinancialYearEndDate = newValue || '';
});

// Financial year period 변경 시 formData 업데이트 및 validation 에러 제거
watch(financialYearPeriod, (newValue) => {
  if (internalValidationErrors.value.financialYearPeriod) {
    delete internalValidationErrors.value.financialYearPeriod;
  }
  formData.value.financialYearPeriod = newValue || '';
});

// ========== Task Flow ==========
const taskFlow = ref({
  all: false,
  createDocument: false,
  requestTSign: false,
  proceedWithEFiling: false
});

// Task Flow 지원 여부 (PROCESS_STEPS 기반)
const isCreateDocumentSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some((step: string) => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'doc' || lowerStep === 'document' || lowerStep.includes('doc');
  });
});

const isRequestTSignSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some((step: string) => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 't-sign' || lowerStep === 'tsign' || lowerStep.includes('sign');
  });
});

const isProceedWithEFilingSupported = computed(() => {
  // SG가 아니면 e-Filing 지원 안 함
  const customerInfo = sessionStore.getCustomerInfo();
  const jurisdiction = customerInfo?.jurisdiction || '';
  let countryCode = '';

  // jurisdiction이 2자리 대문자면 국가코드로 간주
  if (jurisdiction && jurisdiction.length === 2 && /^[A-Z]{2}$/.test(jurisdiction.toUpperCase())) {
    countryCode = jurisdiction.toUpperCase();
  }

  // SG가 아니면 e-Filing 비활성화
  if (!countryCode || countryCode !== 'SG') {
    return false;
  }

  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some((step: string) => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'e-filing' || lowerStep.includes('filing');
  });
});

const isAllSupported = computed(() => {
  return isCreateDocumentSupported.value &&
         isRequestTSignSupported.value &&
         isProceedWithEFilingSupported.value;
});

// Task Flow 상태 업데이트 함수
const updateTaskFlowState = () => {
  taskFlow.value.createDocument = isCreateDocumentSupported.value;
  taskFlow.value.requestTSign = isRequestTSignSupported.value;
  taskFlow.value.proceedWithEFiling = isProceedWithEFilingSupported.value;
  taskFlow.value.all = isAllSupported.value;
  
  emit('update:taskFlow', { ...taskFlow.value });
};

// Task Flow 변경 시 상위 컴포넌트에 전달
watch(taskFlow, (newValue) => {
  emit('update:taskFlow', { ...newValue });
}, { deep: true });

// taskConfig 변경 시 Task Flow 자동 업데이트 및 문서 자동 선택
watch(() => props.taskConfig, (newConfig, oldConfig) => {
  if (newConfig) {
    // taskConfig가 실제로 변경되었을 때만 플래그 리셋
    if (newConfig !== oldConfig) {
      hasEmittedAutoSelect = false;
    }
    // taskConfig가 있으면 바로 자동 선택 실행 (Entity 선택 여부와 관계없이)
    autoSelectCategoryAndFiles();
  }
  updateTaskFlowState();
}, { immediate: true });

onMounted(async () => {
  try {
    if (officerAppointmentStore.entities.length === 0) {
      await officerAppointmentStore.fetchEntities();
    }
    await loadFinancialYearPeriodCodes();
    updateTaskFlowState();
  } catch (e) {
    console.warn('[ChangeFyeForm] Failed to fetch entities:', e);
  }
});

// 외부에서 폼 데이터 복원 (EFilingPage에서 TASK_DATA로 화면 복원용)
const setFormData = async (data: any) => {
  if (!data) return;

  // EFilingPage에서 내려오는 구조: { formData: {...} } 또는 { formFields: {...} } 등을 폭넓게 지원
  const rawFormData = data?.formData || data;
  const fields = rawFormData?.formFields || rawFormData?.formFieldsMap || rawFormData || {};

  const entityVal = await resolveEntityCdFromFormData(rawFormData, fields);
  const revisedVal =
    fields.revisedFinancialYearEndDate ||
    rawFormData?.revisedFinancialYearEndDate ||
    fields.new_fye_date ||
    rawFormData?.new_fye_date ||
    fields.newFyeDate ||
    rawFormData?.newFyeDate ||
    '';
  let periodVal = fields.financialYearPeriod || rawFormData?.financialYearPeriod || '';
  if (!periodVal) {
    const m =
      fields.new_financial_period_months ??
      rawFormData?.new_financial_period_months ??
      fields.newFinancialPeriodMonths ??
      rawFormData?.newFinancialPeriodMonths;
    if (m !== undefined && m !== null && String(m).trim() !== '') {
      periodVal = mapNewFinancialPeriodToB41Code(m);
    }
  }

  // 값 세팅 (watch가 formData 동기화도 수행)
  if (entityVal) entityCd.value = entityVal;
  if (revisedVal) revisedFyeValue.value = revisedVal;
  if (periodVal) financialYearPeriod.value = periodVal;
};

// initialData props가 있으면 자동 복원 (FORM_DATA 병합 지연 시 deep 반영)
watch(
  () => props.initialData,
  async (newData) => {
    if (!newData) return;
    await setFormData(newData);
  },
  { immediate: true, deep: true },
);

// CreateTaskForm.vue에서 데이터 수집 가능하도록 expose
const getFormData = () => {
  const base = {
    entity: formData.value.entityCd,
    revisedFinancialYearEndDate: formData.value.revisedFinancialYearEndDate,
    financialYearPeriod: formData.value.financialYearPeriod
  };

  return {
    ...base,
    // 화면 입력값이 흩어져 보이지 않도록 한 곳에 모아서 저장 (요청사항)
    // contactData처럼 평범한 KEY:VALUE 객체로 유지
    // backend payload의 formData.formFields 로 확인 가능
    formFields: {
      entity: base.entity,
      revisedFinancialYearEndDate: base.revisedFinancialYearEndDate,
      financialYearPeriod: base.financialYearPeriod
    }
  };
};

// E-Filing용 데이터 생성 (ControllerAppointmentForm 패턴 참고)
const getEFilingData = async () => {
  // 세션에서 Client 정보 가져오기
  const customerInfo = sessionStore.getCustomerInfo();
  const filingAgentUEN = customerInfo?.regNo || '';

  // Entity 정보
  const selectedEntity = officerAppointmentStore.entities.find((ent: any) => 
    (ent.entityCd || ent.ENTITY_CD) === entityCd.value
  );
  const entityUEN = selectedEntity?.regNo || selectedEntity?.REG_NO || regNo.value || '';

  // Financial Year Period의 bigo1 값 조회
  let financialYearEndPeriodBigo1 = '';
  const financialYearPeriodCode = formData.value.financialYearPeriod || '';
  if (financialYearPeriodCode) {
    try {
      // B41 코드는 SG 국가코드 사용 (다른 B41 코드들과 동일)
      const response = await baseCodeApi.getCodeByTotalCode('SG', financialYearPeriodCode);
      financialYearEndPeriodBigo1 = (response as any).code?.bigo1 || 
                                     (response as any).data?.code?.bigo1 || 
                                     (response as any).code?.BIGO1 || 
                                     (response as any).data?.code?.BIGO1 || '';
      console.log('📋 [ChangeFyeForm] Financial Year Period BIGO1 조회:', {
        code: financialYearPeriodCode,
        bigo1: financialYearEndPeriodBigo1
      });
    } catch (e) {
      console.warn('Financial Year Period BIGO1 조회 실패:', e);
    }
  }

  // SaveFYEList 구조 생성
  const saveFYE = {
    revisedFinancialYearEndDate: formData.value.revisedFinancialYearEndDate || '',
    financialYearEndPeriod: financialYearEndPeriodBigo1 || financialYearPeriodCode || '',
    changeReasons: [], // 현재 폼에 없으므로 빈 배열
    otherReason: '', // 현재 폼에 없으므로 빈 문자열
    supportingDocuments: [] // 현재 폼에 없으므로 빈 배열
  };

  // E-Filing JSON 구조 (SaveFYEList로 배열을 감싸고, FilingAgentUEN과 EntityUEN은 배열 다음에)
  const eFilingData = {
    SaveFYEList: [saveFYE], // 배열로 감싸기
    FilingAgentUEN: filingAgentUEN,
    EntityUEN: entityUEN
  };

  console.log('📋 [ChangeFyeForm] getEFilingData:', eFilingData);
  return eFilingData;
};

// DOC 변수 치환용 데이터 (CreateTaskForm.vue에서 병합됨)
const getFormDataWithDocVariables = async () => {
  // 필드명(키)을 기준으로 그룹화
  const fieldMap = new Map<string, { value: any; docVariables: string[] }>();

  // 엔티티 기본정보 (entityProfile에서 가져온 값)
  const registrationNoVal = regNo.value || '';
  const companyNameVal = companyName.value || '';
  const financialYearStartVal = financialYearStart.value || '';
  const financialYearEndVal = financialYearEnd.value || '';
  const agmDueDateVal = agmDueDate.value || '';
  const arDueDateVal = arDueDate.value || '';

  // entityName
  if (companyNameVal) {
    fieldMap.set('entityName', {
      value: companyNameVal,
      docVariables: ['CompanyName']
    });
  }

  // entityRegNo
  if (registrationNoVal) {
    fieldMap.set('entityRegNo', {
      value: registrationNoVal,
      docVariables: ['RegistrationNo']
    });
  }

  // FinancialYearStart
  if (financialYearStartVal) {
    fieldMap.set('financialYearStart', {
      value: financialYearStartVal,
      docVariables: ['FinancialYearStart']
    });
  }

  // FinancialYearEnd
  if (financialYearEndVal) {
    fieldMap.set('financialYearEnd', {
      value: financialYearEndVal,
      docVariables: ['FinancialYearEnd']
    });
  }

  // AGMDueDate
  if (agmDueDateVal) {
    fieldMap.set('agmDueDate', {
      value: agmDueDateVal,
      docVariables: ['AGMDueDate']
    });
  }

  // ARDueDate
  if (arDueDateVal) {
    fieldMap.set('arDueDate', {
      value: arDueDateVal,
      docVariables: ['ARDueDate']
    });
  }

  // Revised financial year end date (달력에서 선택한 날짜)
  const revisedFyeIso = revisedFyeValue.value || '';
  const [_, revisedFyeMonth = '', revisedFyeDay = ''] = revisedFyeIso.split('-');
  // 선택한 날짜를 포맷팅
  const revisedFyeDate = revisedFyeIso ? formatDocDate(revisedFyeIso) : '';
  
  // Revised financial year end date의 다음날 계산 (YYYY-MM-DD 형식)
  const nextFyeStartDateIso = revisedFyeIso ? addDaysToIsoDate(revisedFyeIso, 1) : '';
  // dateUtils의 formatDocDate를 사용하여 세션의 dateFormat에 맞춰 포맷팅
  const nextFyeStartDate = nextFyeStartDateIso ? formatDocDate(nextFyeStartDateIso) : '';

  // RevisedFYEMonth
  if (revisedFyeMonth) {
    fieldMap.set('revisedFYEMonth', {
      value: revisedFyeMonth,
      docVariables: ['RevisedFYEMonth']
    });
  }

  // RevisedFYEDay
  if (revisedFyeDay) {
    fieldMap.set('revisedFYEDay', {
      value: revisedFyeDay,
      docVariables: ['RevisedFYEDay']
    });
  }

  // RevisedFYEDate
  if (revisedFyeDate) {
    fieldMap.set('revisedFYEDate', {
      value: revisedFyeDate,
      docVariables: ['RevisedFYEDate']
    });
  }

  // NextFYEStartDate
  if (nextFyeStartDate) {
    fieldMap.set('nextFYEStartDate', {
      value: nextFyeStartDate,
      docVariables: ['NextFYEStartDate']
    });
  }

  // Entity 관련 공통 데이터 조회 및 doc_variables 생성 (useEntityDocVariables 사용)
  if (entityCd.value) {
    try {
      const entityData = await getEntityData(entityCd.value);
      
      // {#CompanyName} - entityName에 추가 (기존 필드와 병합)
      if (entityData.companyName) {
        const existingField = fieldMap.get('entityName');
        if (existingField) {
          if (!existingField.docVariables.includes('CompanyName')) {
            existingField.docVariables.push('CompanyName');
          }
        } else {
          fieldMap.set('entityName', {
            value: entityData.companyName,
            docVariables: ['CompanyName']
          });
        }
      }

      // {#RegistrationNo} - entityRegNo에 추가 (기존 필드와 병합)
      if (entityData.registrationNumber) {
        const existingField = fieldMap.get('entityRegNo');
        if (existingField) {
          if (!existingField.docVariables.includes('RegistrationNo')) {
            existingField.docVariables.push('RegistrationNo');
          }
        } else {
          fieldMap.set('entityRegNo', {
            value: entityData.registrationNumber,
            docVariables: ['RegistrationNo']
          });
        }
      }

      // {#IncorporatedDate} - DATE_OF_INCORPORATION 날짜 형식 (formatDocDate 사용)
      if (entityData.incorporationDate) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.incorporationDate;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('incorporatedDate', {
            value: formattedDate,
            docVariables: ['IncorporatedDate']
          });
        }
      }

      // {#CompanyAddress}
      if (entityData.companyAddress) {
        fieldMap.set('companyAddress', {
          value: entityData.companyAddress,
          docVariables: ['CompanyAddress']
        });
      }

      // {#FinancialYearEnd} - FINANCIAL_YEAR_END 날짜 형식 (formatDocDate 사용)
      if (entityData.financialYearEnd) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.financialYearEnd;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('financialYearEnd', {
            value: formattedDate,
            docVariables: ['FinancialYearEnd']
          });
        }
      }

      // {#PrimaryActivityCode}
      if (entityData.primaryActivityCode) {
        fieldMap.set('primaryActivityCode', {
          value: entityData.primaryActivityCode,
          docVariables: ['PrimaryActivityCode']
        });
      }

      // {#PrimaryActivityDescription}
      if (entityData.primaryActivityDescription) {
        fieldMap.set('primaryActivityDescription', {
          value: entityData.primaryActivityDescription,
          docVariables: ['PrimaryActivityDescription']
        });
      }

      // {#SecondaryActivityCode}
      if (entityData.secondaryActivityCode) {
        fieldMap.set('secondaryActivityCode', {
          value: entityData.secondaryActivityCode,
          docVariables: ['SecondaryActivityCode']
        });
      }

      // {#SecondaryActivityDescription}
      if (entityData.secondaryActivityDescription) {
        fieldMap.set('secondaryActivityDescription', {
          value: entityData.secondaryActivityDescription,
          docVariables: ['SecondaryActivityDescription']
        });
      }

      // {#EntityJurisdiction}
      if (entityData.entityJurisdiction) {
        fieldMap.set('entityJurisdiction', {
          value: entityData.entityJurisdiction,
          docVariables: ['EntityJurisdiction']
        });
      }

      // {#EntityType}
      if (entityData.entityType) {
        fieldMap.set('entityType', {
          value: entityData.entityType,
          docVariables: ['EntityType']
        });
      }

      // {#CompanyEmail}
      if (entityData.companyEmail) {
        fieldMap.set('companyEmail', {
          value: entityData.companyEmail,
          docVariables: ['CompanyEmail']
        });
      }
    } catch (error) {
      console.error('❌ [ChangeFyeForm] Entity 데이터 조회 실패:', error);
    }
  }

  // 결과 객체 생성 - 각 필드를 { fieldName: { value: "...", doc_variables: ["#..."] } } 형태로 변환
  const result: Record<string, any> = {};
  
  fieldMap.forEach((item, fieldName) => {
    // doc_variables를 배열로 변환
    const docVariablesArray = item.docVariables.map(v => `#${v}`);
    
    // 각 필드를 { value: "...", doc_variables: ["#..."] } 형태의 객체로 저장
    result[fieldName] = {
      value: item.value,
      doc_variables: docVariablesArray
    };
  });
  
  return result;
};

// ========== Validation ==========

// Entity validation error messages (computed)
const entityErrorMessages = computed(() => {
  if (props.validationErrors?.entity) {
    return [props.validationErrors.entity];
  }
  if (internalValidationErrors.value.entity) {
    return [internalValidationErrors.value.entity];
  }
  return [];
});

// Revised Financial Year End Date validation error messages (computed)
const revisedFyeErrorMessages = computed(() => {
  if (props.validationErrors?.revisedFinancialYearEndDate) {
    return [props.validationErrors.revisedFinancialYearEndDate];
  }
  if (internalValidationErrors.value.revisedFinancialYearEndDate) {
    return [internalValidationErrors.value.revisedFinancialYearEndDate];
  }
  return [];
});

// Financial Year Period validation error messages (computed)
const financialYearPeriodErrorMessages = computed(() => {
  if (props.validationErrors?.financialYearPeriod) {
    return [props.validationErrors.financialYearPeriod];
  }
  if (internalValidationErrors.value.financialYearPeriod) {
    return [internalValidationErrors.value.financialYearPeriod];
  }
  return [];
});

// Entity 검증 함수
const validateEntity = (): boolean => {
  if (!entityCd.value) {
    internalValidationErrors.value.entity = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      try {
        const wrap = document.querySelector('[data-field="entityCd"]') as HTMLElement | null;
        if (props.eFilingMode) {
          wrap?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        // v-autocomplete의 실제 input 요소 찾기
        const entityInput = wrap?.closest('.v-autocomplete')?.querySelector('input') as HTMLInputElement;
        if (entityInput) {
          entityInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            entityInput.focus();
          }, 100);
        } else if (entityCdRef.value) {
          // ref를 통한 포커스 시도
          const element = entityCdRef.value.$el || entityCdRef.value.$refs?.input?.$el;
          if (element) {
            (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = element.querySelector?.('input') as HTMLInputElement;
            if (input) {
              setTimeout(() => {
                input.focus();
              }, 100);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to focus entity input:', error);
      }
    });
    return false;
  }
  if (internalValidationErrors.value.entity) {
    delete internalValidationErrors.value.entity;
  }
  return true;
};

// Revised Financial Year End Date 검증 함수
const validateRevisedFinancialYearEndDate = (): boolean => {
  if (!revisedFyeValue.value) {
    internalValidationErrors.value.revisedFinancialYearEndDate = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      try {
        // readonly 필드이므로 날짜 선택 메뉴를 먼저 열기
        revisedFyeMenu.value = true;
        
        // 텍스트 필드 영역으로 스크롤
        const dateField = document.querySelector('[data-field="revisedFye"]') as HTMLElement;
        if (dateField) {
          dateField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // 텍스트 필드에 포커스 스타일 적용을 위해 클릭 이벤트 시뮬레이션
          setTimeout(() => {
            const textField = dateField.querySelector('.v-text-field') as HTMLElement;
            if (textField) {
              // readonly이지만 포커스 효과를 위해 클래스 추가
              const input = textField.querySelector('input') as HTMLInputElement;
              if (input) {
                // 포커스 이벤트 발생
                input.dispatchEvent(new Event('focus', { bubbles: true }));
              }
            }
          }, 150);
        }
      } catch (error) {
        console.warn('Failed to focus revised FYE input:', error);
      }
    });
    return false;
  }
  if (internalValidationErrors.value.revisedFinancialYearEndDate) {
    delete internalValidationErrors.value.revisedFinancialYearEndDate;
  }
  return true;
};

// Financial Year Period 검증 함수
const validateFinancialYearPeriod = (): boolean => {
  if (!financialYearPeriod.value) {
    internalValidationErrors.value.financialYearPeriod = t('startTask.This field is required') || 'This field is required';
    nextTick(() => {
      try {
        // 첫 번째 라디오 버튼에 포커스
        const periodButtons = document.querySelector('.period-buttons');
        if (periodButtons) {
          (periodButtons as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          const firstRadio = periodButtons.querySelector('.period-button:first-child input[type="radio"]') as HTMLInputElement;
          if (firstRadio) {
            setTimeout(() => {
              firstRadio.focus();
            }, 100);
          } else {
            // 라디오 버튼을 찾을 수 없으면 첫 번째 period-button에 포커스
            const firstButton = periodButtons.querySelector('.period-button:first-child') as HTMLElement;
            if (firstButton) {
              setTimeout(() => {
                firstButton.focus();
              }, 100);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to focus financial year period input:', error);
      }
    });
    return false;
  }
  if (internalValidationErrors.value.financialYearPeriod) {
    delete internalValidationErrors.value.financialYearPeriod;
  }
  return true;
};

// 전체 필수 필드 검증 함수
const validateAll = (): boolean => {
  internalValidationErrors.value = {};
  
  const isEntityValid = validateEntity();
  if (!isEntityValid) {
    return false;
  }
  
  const isRevisedFyeValid = validateRevisedFinancialYearEndDate();
  if (!isRevisedFyeValid) {
    return false;
  }
  
  const isPeriodValid = validateFinancialYearPeriod();
  if (!isPeriodValid) {
    return false;
  }
  
  return true;
};

/** 좌측 챗 Entity lookup 선택 → 폼 엔티티 반영 */
function applyEntityFromChatSelection(ent: EntityItem) {
  const cd = String(ent?.entityCd || '').trim();
  if (!cd) return;
  entityCd.value = cd;
}

defineExpose({
  entity: entityCd,
  formData, // 화면에서 입력된 모든 정보를 저장하는 객체
  setFormData, // EFilingPage에서 TASK_DATA로 화면 복원용
  getFormData,
  getFormDataWithDocVariables,
  getEFilingData, // E-Filing용 데이터 생성
  validateEntity, // Entity 검증 함수
  validateRevisedFinancialYearEndDate, // Revised Financial Year End Date 검증 함수
  validateFinancialYearPeriod, // Financial Year Period 검증 함수
  validateAll, // 전체 필수 필드 검증 함수
  applyEntityFromChatSelection,
});
</script>

<template>
  <div class="change-fye-wrapper">
    <div class="mb-6" style="max-width: 70%;" data-field="entityCd">
      <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
        {{ t('startTask.Entity Name') || 'Entity Name' }} <span class="text-error">*</span>
      </label>
      <v-autocomplete
        ref="entityCdRef"
        v-model="entityCd"
        :items="officerAppointmentStore.entityOptions"
        :placeholder="t('startTask.Search') || 'Search'"
        variant="outlined"
        density="compact"
        :error-messages="entityErrorMessages"
        append-inner-icon="mdi-magnify"
        clearable
        :disabled="props.readOnly"
      />
    </div>

    <!-- Entity summary (read-only, from L_ENTITY) -->
    <div v-if="entityCd" class="entity-summary" style="max-width: 70%;">
      <div class="summary-row">
        <div class="summary-label">{{ t('startTask.Entity UEN') || 'Entity UEN' }}</div>
        <div class="summary-value">{{ regNo || '-' }}</div>
      </div>
      <div class="summary-row">
        <div class="summary-label">{{ t('startTask.Entity name') || 'Entity name' }}</div>
        <div class="summary-value">{{ companyName || '-' }}</div>
      </div>
      <div class="summary-row">
        <div class="summary-label">{{ t('startTask.Financial year start date') || 'Financial year start date' }}</div>
        <div class="summary-value">{{ financialYearStart || '-' }}</div>
      </div>
      <div class="summary-row">
        <div class="summary-label">{{ t('startTask.Current financial year end date') || 'Current financial year end date' }}</div>
        <div class="summary-value">{{ financialYearEnd || '-' }}</div>
      </div>
      <div class="summary-row">
        <div class="summary-label">{{ t('startTask.Next annual general meeting due date') || 'Next annual general meeting due date' }}</div>
        <div class="summary-value">{{ agmDueDate || '-' }}</div>
      </div>
      <div class="summary-row">
        <div class="summary-label">{{ t('startTask.Next annual return due date') || 'Next annual return due date' }}</div>
        <div class="summary-value">{{ arDueDate || '-' }}</div>
      </div>
    </div>

    <!-- Revised FYE date (calendar) -->
    <div v-if="entityCd" class="mt-6" style="max-width: 70%;" data-field="revisedFye">
      <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
        {{ t('startTask.Revised financial year end date') || 'Revised financial year end date' }} <span class="text-error">*</span>
      </label>
      <v-menu
        v-model="revisedFyeMenu"
        :close-on-content-click="false"
        location="bottom"
        :disabled="props.readOnly"
      >
        <template v-slot:activator="{ props: menuProps }">
          <v-text-field
            ref="revisedFyeInputRef"
            v-bind="menuProps"
            :model-value="revisedFyeDisplayFormatted"
            variant="outlined"
            density="compact"
            :error-messages="revisedFyeErrorMessages"
            readonly
            append-inner-icon="mdi-calendar"
            :disabled="props.readOnly"
          />
        </template>
        <v-date-picker
          v-model="revisedFyePickerValue"
          v-bind="datePickerProps"
          @update:model-value="revisedFyeMenu = false"
          :disabled="props.readOnly"
        />
      </v-menu>
    </div>

    <!-- Financial year period (radio buttons) -->
    <div v-if="entityCd" class="mt-6" style="max-width: 70%;">
      <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
        {{ t('startTask.Financial year period') || 'Financial year period' }} <span class="text-error">*</span>
      </label>

      <div class="period-buttons" v-if="financialYearPeriodOptions.length">
        <div
          v-for="opt in financialYearPeriodOptions"
          :key="opt.value"
          class="period-button"
          :class="{ 
            active: financialYearPeriod === opt.value,
            'error': financialYearPeriodErrorMessages.length > 0
          }"
          @click="() => { if (!props.readOnly) financialYearPeriod = opt.value; }"
        >
          <v-radio
            :model-value="financialYearPeriod"
            :value="opt.value"
            hide-details
            :disabled="props.readOnly"
          />
          <span>{{ opt.title }}</span>
        </div>
      </div>

      <div v-else class="text-caption" style="color: #777E85;">
        {{ isLoadingFinancialYearPeriod ? (t('startTask.Loading') || 'Loading...') : '-' }}
      </div>

      <!-- Financial Year Period 에러 메시지 -->
      <div v-if="financialYearPeriodErrorMessages.length > 0" class="text-error text-caption mt-1">
        {{ financialYearPeriodErrorMessages[0] }}
      </div>
    </div>

    <!-- Task Flow Section -->
    <TaskFlowSection
      v-if="entityCd && props.taskConfig && props.showTaskFlow"
      v-model="taskFlow"
      :visible="true"
      :is-all-supported="isAllSupported"
      :is-create-document-supported="isCreateDocumentSupported"
      :is-request-t-sign-supported="isRequestTSignSupported"
      :is-proceed-with-e-filing-supported="isProceedWithEFilingSupported"
      class="mt-6"
      style="max-width: 70%;"
    />
  </div>
</template>

<style scoped lang="scss">
.change-fye-wrapper {
  margin-bottom: 24px;
}

.text-error {
  color: rgb(var(--v-theme-error));
}

.entity-summary {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.summary-row {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 16px;
  align-items: start;
}

.summary-label {
  font-size: 14px;
  color: #777E85;
}

.summary-value {
  font-size: 14px;
  color: #111827;
  font-weight: 600;
}

.period-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.period-button {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.period-button.active {
  border-color: #1B84FF;
  background-color: rgba(27, 132, 255, 0.06);
}

.period-button.error {
  border-color: rgb(var(--v-theme-error));
}

.period-button span {
  font-size: 14px;
  color: #111827;
  font-weight: 500;
}
</style>

